import { createTheme } from "@mui/material/styles";

// Brand palette derived from the SRCS/Sesametech logo red, softened for UI use:
// a vivid coral-red for interactive accents, a muted clay tone for large
// surfaces (see the "SRCS Dashboard Redesign" canvas for the full rationale).
const brand = {
  main: "#E5484D",
  dark: "#C93E43",
  light: "#FBE4E3",
  surface: "#C8584F",
  contrastText: "#FFFFFF",
};

const theme = createTheme({
  palette: {
    primary: brand,
    secondary: {
      main: "#8B8698",
      dark: "#6B6773",
      light: "#F1EFF9",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#FBF8F7",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#211D2E",
      secondary: "#6B6773",
    },
    divider: "#F0E4E3",
    success: { main: "#1B8A4B" },
    warning: { main: "#B7791F" },
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: "none" },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        outlined: {
          borderColor: "#F0E4E3",
          boxShadow: "0 1px 2px rgba(23,17,51,0.04)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid #F0E4E3",
          boxShadow: "0 1px 2px rgba(23,17,51,0.04)",
          overflow: "hidden",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          boxShadow: "none",
        },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #F5EEED",
        },
        head: {
          background: "#FDF9F8",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          color: "#9C97AA",
          textTransform: "uppercase",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          border: "1px solid #F0E4E3",
          borderRadius: 12,
          padding: "10px 14px",
          backgroundColor: "#FFFFFF",
          "&:hover:not(.Mui-disabled)": { borderColor: "#E5A9A7" },
          "&.Mui-focused": { borderColor: "#E5484D", boxShadow: "0 0 0 1px #E5484D" },
          "&.Mui-disabled": { backgroundColor: "#FDF9F8", borderColor: "#F0E4E3" },
        },
        input: {
          padding: 0,
          "&::placeholder": { color: "#B6B0C6", opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          position: "static",
          transform: "none",
          fontSize: 12,
          fontWeight: 600,
          color: "#4B4658",
          marginBottom: 6,
          "&.Mui-focused": { color: "#E5484D" },
          "&.Mui-disabled": { color: "#9C97AA" },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": { color: "#E5484D" },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "#6B6773",
        },
        toolbar: {
          minHeight: 52,
          paddingLeft: 16,
        },
        selectLabel: {
          fontSize: 12.5,
          color: "#9C97AA",
        },
        displayedRows: {
          fontSize: 12.5,
          color: "#9C97AA",
        },
        select: {
          borderRadius: 8,
        },
        actions: {
          color: "#4B4658",
        },
      },
    },
  },
});

export default theme;
